// What the phone part drawer says about a part: brand line, model name and the
// three figures a rider compares for that step (weight, a step-specific spec,
// price). Wheel rim depths come from src/data/wheel-specs.json (sourced).
import { partWeight } from './weight.js';
import { entryPrice } from './composition.js';
import { isExtremaCockpit } from './threeTCockpit.js';
import { itemImage, imageUrl } from './index.js';
import wheelSpecs from '../data/wheel-specs.json';
import tyreTread from '../data/tyre-tread.json';

const number = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
export const grams = value => `${number.format(Math.round(value))}\u00a0g`;
const cents = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Whole euros stay whole (4.999 €); anything with cents shows both digits (199,80 €).
export const euros = value => {
  const rounded = Math.round(value * 100) / 100;
  return `${(Number.isInteger(rounded) ? number : cents).format(rounded)}\u00a0€`;
};

// Brands longer than one word, and names stored with a short or misspelt brand.
const MULTI_WORD = ['Black Inc', 'Princeton Carbonworks'];
const ALIASES = { Conti: 'Continental', Ligthweight: 'Lightweight', Sram: 'SRAM', Enve: 'ENVE' };
const PAIR_STEPS = ['wheels', 'tyres', 'cages', 'pedals'];

export function splitName(name = '') {
  const clean = name.replace(/\s+/g, ' ').trim();
  if (/^generic\b/i.test(clean)) return { brand: '', model: clean };
  const multi = MULTI_WORD.find(brand => clean.toLowerCase().startsWith(brand.toLowerCase() + ' '));
  const brand = multi || clean.split(' ')[0];
  const model = clean.slice(brand.length).trim();
  return model ? { brand: ALIASES[brand] || brand, model } : { brand: '', model: clean };
}

// One wheel's depth in mm: 'front' or 'rear'.
export function rimDepthMm(item, end) {
  const spec = item && wheelSpecs[item.name];
  if (!spec || !spec.depthFront) return null;
  return end === 'rear' && spec.depthRear || spec.depthFront;
}

// What a step's finishes are called next to their chips.
export const finishLabel = id => ({ tyres: 'Tyre colour', framesets: 'Finish', wheels: 'Decals' })[id] || 'Colour';

// The wheel spotlight's figures; any we have no sourced value for are left out.
export function wheelFigures(entry) {
  const { item } = entry;
  const spec = wheelSpecs[item.name] || {};
  const weight = partWeight(entry);
  const price = entryPrice(entry);
  return {
    depth: rimDepth(item),
    inner: spec.internalWidth ? `${spec.internalWidth} mm` : null,
    weight: weight ? grams(weight.grams) : null,
    price: price ? euros(price) : null
  };
}

// The tyre spotlight's figures. widths: the widths in stock for this finish.
export function tyreFigures(entry, widths = []) {
  const weight = partWeight(entry);
  const price = entryPrice(entry);
  return {
    width: entry.tyreWidth ? `${entry.tyreWidth} mm` : widths.length ? `${widths.join(' · ')} mm` : null,
    widthLabel: entry.tyreWidth ? 'Width' : 'Widths in stock',
    type: entry.item.category || null,
    weight: weight ? grams(weight.grams) : null,
    price: price ? euros(price) : null
  };
}

// The maker's tread shot for the tyre (src/data/tyre-tread.json, by name),
// in the chosen finish when one was sourced (e.g. a tan wall).
export function treadImage(item, colourName) {
  const tread = item && tyreTread[(item.name || '').trim()];
  if (!tread) return '';
  // A finish without its own photo uses the default one, if any (e.g. the
  // P-Zero Velo is only photographed with yellow logos: no default).
  const file = tread.variants && tread.variants[colourName] || tread.file;
  if (!file) return '';
  return imageUrl(`/resources/tyres/tread/${file}`);
}

export function rimDepth(item) {
  const spec = item && wheelSpecs[item.name];
  if (!spec || !spec.depthFront) return null;
  const rear = spec.depthRear || spec.depthFront;
  return rear === spec.depthFront ? `${spec.depthFront} mm` : `${spec.depthFront} / ${rear} mm`;
}

const included = item => item.name === 'SEKA Gladius' || isExtremaCockpit(item.name);

// The middle figure: what distinguishes parts within this step.
function stepSpec(entry, extra) {
  const { item } = entry;
  const id = entry.props && entry.props.id;
  const choice = item.colors && item.colors[entry.color];
  const colour = choice && choice.color && choice.color.colorName;
  if (id === 'wheels') return { label: 'Rim depth', value: rimDepth(item) || '—' };
  if (id === 'tyres') return { label: 'Width', value: entry.tyreWidth ? `${entry.tyreWidth} mm` : 'Choose' };
  if (id === 'groupsets' && item.category) return { label: 'Drivetrain', value: item.category === 'Gravel' ? '1×' : '2×' };
  if (id === 'framesets' && extra.tyreLimit) return { label: 'Tyre limit', value: `${extra.tyreLimit} mm` };
  return { label: 'Colour', value: colour || '—' };
}

// entry: a build selection entry ({ props: step, item, color, tyreWidth }).
export function partSpecs(entry, extra = {}) {
  if (!entry || !entry.item) return null;
  const { item } = entry;
  const id = entry.props && entry.props.id;
  const none = item.noCages || item.noPedals;
  const { brand, model } = none ? { brand: '', model: item.name } : splitName(item.name);
  const choice = item.colors && item.colors[entry.color];
  const weight = partWeight(entry);
  const price = entryPrice(entry);
  return {
    brand,
    model,
    colour: !none && choice && choice.color && choice.color.colorName || '',
    url: !none && choice && choice.url || '',
    stats: none ? [] : [
      { label: PAIR_STEPS.includes(id) ? 'Pair weight' : id === 'framesets' ? 'Frameset' : 'Weight', value: weight ? grams(weight.grams) : '—' },
      stepSpec(entry, extra),
      { label: 'Price', value: included(item) ? 'Included' : price ? euros(price) : '—' }
    ]
  };
}

// The image a drawer row shows for the step, like the step's cards (BikeItems).
// chosen: the row is the picked part, so show the picked finish. A frameset's
// catalogue thumbnail is one picture for every finish; its finish artwork isn't.
export function rowImage(type, item, color = 0, chosen = false) {
  const image = itemImage(item, color);
  if (chosen && type === 'Frameset' && image.src) return image.src;
  if (type === 'Pedals') return image.front || image.thumb || image.src;
  if (type === 'Groupset') return image.front || image.thumb;
  if (['Saddles', 'Wheels', 'Tyres'].includes(type)) return image.src || image.front || image.thumb;
  if (type === 'Bars/Stem') return image.thumb || image.src;
  return image.thumb || image.front || image.src;
}

// One list row's figures, at the part's cheapest finish (what the row shows).
export function rowLine(step, item) {
  if (item.noCages || item.noPedals) return '';
  const prices = (item.colors || []).map(a => a.color && a.color.price || a.price || 0).filter(p => p > 0);
  const min = prices.length ? Math.min(...prices) : 0;
  const weight = partWeight({ props: step, item, color: 0 });
  const from = prices.length > 1 && Math.max(...prices) > min ? 'from ' : '';
  return [
    step.id === 'wheels' && rimDepth(item),
    weight && grams(weight.grams),
    included(item) ? 'Included' : min ? from + euros(min) : ''
  ].filter(Boolean).join(' · ');
}
